import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { Link } from '@mui/material';
import { format, parseISO } from 'date-fns';
import { useContext } from 'react';

import { UserContext } from '~/auth/userContext';
import { FieldRow, FieldValue } from '~/components/common/detailFields';
import { HeaderField } from '~/components/common/HeaderField';
import type { API_Compliance } from '~/data/schemas/compliance';

const displayDate = (value: string | null | undefined) =>
  value ? format(parseISO(value), 'MMM d, yyyy') : null;

/**
 * The certificate itself — the Certificate card's body on the Compliance tab. Only what
 * belongs to the certificate and to nothing else: downloading and replacing it are
 * buttons in the card's heading, the inspection status is a chip beside the car's name,
 * the violation counts are the toggle below, and the CAT tests are Edit Unit's fields.
 *
 * UK (en-GB) reads a different set. The drawer this card replaced carried it from
 * CX-416 and AMP-687; it was never built here, so when the `amp-redesign` flag's
 * default variation flipped to `true` on 2026-09-02 every UK consultant lost these
 * three fields. (AMP-741)
 */
export function ElevatorComplianceFields({
  compliance,
  showDownload = false,
}: {
  compliance: API_Compliance;
  /** For the row preview, which has no Certificate card heading to carry the button. */
  showDownload?: boolean;
}) {
  const { locale } = useContext(UserContext);
  const isUK = locale === 'en-GB';

  const download =
    showDownload && compliance.downloadUrl ? (
      <Link
        href={compliance.downloadUrl}
        target="_blank"
        rel="noopener"
        underline="hover"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
      >
        <FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />
        Download certificate
      </Link>
    ) : null;

  return (
    <FieldRow>
      {isUK ? (
        <HeaderField label="Next Examination" caption={download}>
          {/*
            UK reads the next examination date off the latest LOLER report, not the
            synthetic certificate — the same source the overdue / due-soon tiles count.
            See the `next_inspection_due` serializer field for why the two used to
            disagree. (CX-416)

            That disagreement is the point: a UK consultant working from the overdue
            tiles and reading "Expires" is looking at two different dates.
          */}
          <FieldValue loaded value={displayDate(compliance.nextInspectionDue)} />
        </HeaderField>
      ) : (
        <HeaderField label="Expires" caption={download}>
          <FieldValue
            loaded
            value={displayDate(compliance.certificateExpiration)}
            fallback="No expiration on file"
          />
        </HeaderField>
      )}

      {isUK ? (
        <HeaderField label="Last Examination">
          {/*
            The latest LOLER report's own inspection date — when the lift was last
            actually examined. Reads `examinationDate` (backed by
            `latest_examination_date`, sourced off the latest report regardless of
            whether it stated a next-due date), not `nextInspectionDue` above. (AMP-687)
          */}
          <FieldValue loaded value={displayDate(compliance.examinationDate)} />
        </HeaderField>
      ) : null}

      {isUK ? (
        /*
          The report-level "LOLER Code" field used to sit here. It rendered the
          inspection company's own outcome code (Zurich/Crimson's `Item Status`:
          OK/BD/AN/NV/DL), which is one vendor's private vocabulary and precisely what
          customers asked us to replace with a generic category. Each defect now carries
          its own A/B/C category, shown per entry in the defect list below. (CX-416)

          The report number stays: it is how consultants reconcile a defect against the
          inspection company that raised it, and outside the upload form nothing else in
          the product shows it.
        */
        <HeaderField label="Report Number">
          <FieldValue loaded value={compliance.reportNumber} />
        </HeaderField>
      ) : null}

      <HeaderField label="Jurisdiction">
        <FieldValue loaded value={compliance.jurisdiction} fallback="N/A" />
      </HeaderField>
    </FieldRow>
  );
}
